import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, PlusCircle, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/button';
import { generateRebalancingAdvice } from '../utils/allocationMath';

export default function RebalancingAdvice({ summary }) {
  const [newCapital, setNewCapital] = useState(50000);
  const advices = generateRebalancingAdvice(summary);

  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  // Simulation du déploiement des nouveaux capitaux selon la cible
  const target = summary.targetPercentages;
  const simulatedDeployment = [
    { label: "Liquidités & Sécurisé", pct: target.liquidites, amount: (newCapital * target.liquidites) / 100, color: "#0088FE" },
    { label: "Placements Financiers Cotés", pct: target.placements, amount: (newCapital * target.placements) / 100, color: "#00C49F" },
    { label: "Immobilier & Foncier", pct: target.immobilier, amount: (newCapital * target.immobilier) / 100, color: "#FFBB28" },
    { label: "Alternatifs & Private Equity", pct: target.alternatif, amount: (newCapital * target.alternatif) / 100, color: "#8B5CF6" },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Recommandations de rééquilibrage */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex items-center gap-2 pb-3 border-b border-workspace mb-4">
          <Lightbulb className="w-5 h-5 text-warning-500" />
          <h3 className="text-base font-bold text-primary">Préconisations Stratégiques d'Arbitrage</h3>
        </div>

        <div className="space-y-3">
          {advices.map((advice, index) => {
            const isWarning = advice.severity === 'warning';
            const isSuccess = advice.severity === 'success';

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={`p-4 rounded-xl border text-sm flex items-start gap-3.5 ${
                  isWarning 
                    ? "bg-warning-50/60 dark:bg-warning-900/10 border-warning-200 dark:border-warning-800/40 text-warning-900 dark:text-warning-200" 
                    : isSuccess 
                    ? "bg-success-50/60 dark:bg-success-900/10 border-success-200 dark:border-success-800/40 text-success-900 dark:text-success-200"
                    : "bg-info-50/60 dark:bg-info-900/10 border-info-200 dark:border-info-800/40 text-info-900 dark:text-info-200"
                }`}
              >
                {isWarning ? (
                  <AlertCircle className="w-5 h-5 text-warning-600 shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-success-600 shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-bold uppercase tracking-wider text-xs">
                      {advice.category} • {advice.type}
                    </span>
                    {advice.amount > 0 && (
                      <span className="font-extrabold text-xs px-2 py-0.5 rounded bg-surface border border-workspace">
                        Impact : {formatEuro(advice.amount)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs leading-relaxed opacity-90">{advice.message}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* 2. Simulateur d'injection de nouveaux capitaux */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5 text-secondary" />
            <div>
              <h3 className="text-base font-bold text-primary">Simulateur d'Injection de Nouveaux Capitaux</h3>
              <p className="text-xs text-subtle">Simulez le déploiement optimal d'une rentrée de fonds (vente, héritage, dividendes).</p>
            </div>
          </div>
          <div className="w-full sm:w-64">
            <Input
              type="number"
              min="0"
              step="5000"
              value={newCapital || ''}
              onChange={(e) => setNewCapital(Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="Montant à injecter..."
              className="font-bold text-right"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {simulatedDeployment.map((item, idx) => (
            <div key={idx} className="p-4 rounded-xl bg-surface-muted/30 border border-workspace flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs font-semibold text-subtle">{item.label}</span>
                </div>
                <p className="text-xl font-black text-primary tracking-tight">{formatEuro(item.amount)}</p>
              </div>
              <div className="mt-3 pt-2 border-t border-workspace flex items-center justify-between text-[11px] text-subtle">
                <span>Clé cible : <strong>{item.pct}%</strong></span>
                <ArrowRight className="w-3 h-3 text-secondary" />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-5 p-4 rounded-xl bg-secondary/5 border border-secondary/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="text-primary">
            <strong>Nouveau Total Projeté :</strong> {formatEuro(summary.totalActifsBruts + newCapital)} (dont {formatEuro(newCapital)} d'apport additionnel).
          </div>
          <span className="text-secondary font-bold">
            Répartition automatique alignée sur le profil {summary.targetConfig.label}
          </span>
        </div>
      </div>
    </div>
  );
}

